<template>
    <div class="modal-backdrop" @click.self="$emit('close')">
        <div class="modal">
            <div class="modal-header">
                <h3>Notes for {{ formatDate(event.event_date) }}</h3>
                <button class="close-button" @click="$emit('close')">&times;</button>
            </div>
            <div class="modal-body">
                <div v-if="loading" class="loading-spinner">Loading...</div>
                <div v-else>
                    <div class="notes-list" v-if="notes.length > 0">
                        <div v-for="note in notes" :key="note.id" class="note-item">
                            <div class="note-header">
                                <div class="note-meta">
                                    <span class="note-date">{{ formatDate(note.createdAt) }}</span>
                                    <span class="note-privacy-indicator" :class="{ 'private': note.is_private ?? note.isPrivate ?? true }">
                                        {{ (note.is_private ?? note.isPrivate ?? true) ? 'Private' : 'Public' }}
                                    </span>
                                </div>
                                <div class="note-actions">
                                    <button @click="editNote(note)" :class="{ 'active': editingNote?.id === note.id }">
                                        Edit
                                    </button>
                                    <button @click="deleteNote(note)" class="delete">
                                        Delete
                                    </button>
                                </div>
                            </div>
                            <div v-if="editingNote?.id === note.id" class="note-editor">
                                <rich-text-editor
                                    v-model="editingNote.content"
                                    :placeholder="'Edit your note...'"
                                />
                                <div class="note-privacy">
                                    <label>
                                        <input type="checkbox" v-model="editingNote.isPrivate">
                                        Private note (only visible to you)
                                    </label>
                                </div>
                                <div class="editor-actions">
                                    <button @click="cancelEdit">Cancel</button>
                                    <button @click="saveEdit" class="primary">Save</button>
                                </div>
                            </div>
                            <div v-else class="note-content">{{ renderContent(note.content) }}</div>
                        </div>
                    </div>

                    <div v-if="!isEditing" class="new-note-section">
                        <button @click="startNewNote" class="new-note-btn">
                            + Add New Note
                        </button>
                    </div>

                    <div v-if="isCreatingNew" class="new-note-editor">
                        <rich-text-editor
                            v-model="newNote.content"
                            :placeholder="'Write your note...'"
                        />
                        <div class="note-privacy">
                            <label>
                                <input type="checkbox" v-model="newNote.isPrivate">
                                Private note (only visible to you)
                            </label>
                        </div>
                        <div class="editor-actions">
                            <button @click="cancelNewNote">Cancel</button>
                            <button @click="saveNewNote" class="primary">Save</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <TaskExtractorModal 
        v-if="showTaskExtractor" 
        :noteId="taskExtractionNoteId"
        :content="taskExtractionContent"
        :mentionedUsers="taskExtractionUsers"
        :show="showTaskExtractor"
        @close="showTaskExtractor = false"
        @tasks-created="handleTasksCreated"
        @error="handleTaskError"
    />
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue';
import { formatDate } from '../../utils/dateUtils';
import { noteService } from '../../services/noteService';
import RichTextEditor from './RichTextEditor.vue';
import TaskExtractorModal from '../tasks/TaskExtractorModal.vue';

export default defineComponent({
    name: 'NotesModal',
    components: {
        RichTextEditor,
        TaskExtractorModal
    },
    emits: ['close', 'tasks-created'],
    props: {
        event: {
            type: Object,
            required: true
        }
    },
    setup(props, { emit }) {
        const notes = ref([]);
        const loading = ref(true);
        const editingNote = ref(null);
        const isCreatingNew = ref(false);
        // Initialize new note with isPrivate true by default
        const newNote = ref({
            content: { type: 'doc', content: [{ type: 'paragraph' }] },
            isPrivate: true,
            is_private: true
        });
        
        const showTaskExtractor = ref(false);
        const taskExtractionNoteId = ref(null);
        const taskExtractionContent = ref(null);
        const taskExtractionUsers = ref([]);

        const isEditing = computed(() => editingNote.value !== null || isCreatingNew.value);

        const renderContent = (content) => {
            if (typeof content === 'string') {
                try {
                    content = JSON.parse(content);
                } catch (e) {
                    return content;
                }
            }
            
            if (!content || !content.content) return '';
            
            return content.content.reduce((text, node) => {
                if (node.type === 'paragraph') {
                    const paragraphText = node.content?.reduce((innerText, textNode) => {
                        if (textNode.type === 'mention') {
                            return innerText + `@${textNode.attrs.label}`;
                        }
                        return innerText + (textNode.text || '');
                    }, '') || '';
                    return text + paragraphText + '\n';
                }
                return text;
            }, '').trim();
        };

        const loadNotes = async () => {
            try {
                const rawNotes = await noteService.fetchNotesForEntity('event', props.event.id);
                // Transform snake_case to camelCase
                notes.value = rawNotes.map(note => ({
                    ...note,
                    createdAt: note.created_at,
                    updatedAt: note.updated_at,
                    deletedAt: note.deleted_at,
                    authorId: note.author_id,
                    authorName: note.author_name,
                    isPrivate: note.is_private,
                    noteType: note.note_type,
                    createdBy: note.created_by,
                    updatedBy: note.updated_by
                }));
            } catch (error) {
                console.error('Error loading notes:', error);
                // TODO: Add error handling
            } finally {
                loading.value = false;
            }
        };

        const startNewNote = () => {
            isCreatingNew.value = true;
            // Reset with isPrivate true
            newNote.value = {
                content: { type: 'doc', content: [{ type: 'paragraph' }] },
                isPrivate: true,
                is_private: true
            };
        };

        const cancelNewNote = () => {
            isCreatingNew.value = false;
            // Reset with isPrivate true
            newNote.value = {
                content: { type: 'doc', content: [{ type: 'paragraph' }] },
                isPrivate: true,
                is_private: true
            };
        };

        const saveNewNote = async () => {
            try {
                
                const response = await noteService.createNote({
                    content: newNote.value.content,
                    entityType: 'event',
                    entityId: props.event.id,
                    noteType: 'eventNote',
                    isPrivate: newNote.value.isPrivate,  
                    is_private: newNote.value.isPrivate,  
                    tags: []
                });

                // Extract tasks from the newly created note first
                if (response && response.note) {
                    const savedNote = response.note;
                    
                    // Create a properly structured note object for task extraction
                    const noteForExtraction = {
                        id: savedNote.id,
                        content: typeof savedNote.content === 'string' ? 
                            JSON.parse(savedNote.content) : 
                            savedNote.content
                    };
                    await extractTasks(noteForExtraction);
                } else {
                    console.error('Invalid API response structure:', response);
                }

                // Then reload notes and reset the form
                await loadNotes();
                isCreatingNew.value = false;
                newNote.value = {
                    content: { type: 'doc', content: [{ type: 'paragraph' }] },
                    isPrivate: true,
                    is_private: true
                };
            } catch (error) {
                console.error('Error creating note:', error);
            }
        };

        const editNote = (note) => {
            const content = typeof note.content === 'string' ? JSON.parse(note.content) : note.content;
            // Handle both snake_case from DB and camelCase from frontend
            const isPrivate = note.is_private ?? note.isPrivate ?? true;
            
            editingNote.value = { 
                ...note,
                content: content,
                isPrivate: isPrivate,
                is_private: isPrivate  // Ensure both cases are set
            };
        };

        const cancelEdit = () => {
            editingNote.value = null;
        };

        const saveEdit = async () => {
            if (!editingNote.value) return;

            try {
                const noteId = editingNote.value.id;
                const content = editingNote.value.content;

                await noteService.updateNote(noteId, {
                    content,
                    noteType: editingNote.value.noteType || 'eventNote',
                    isPrivate: editingNote.value.isPrivate ?? true,  // Default to true if not set
                    is_private: editingNote.value.isPrivate ?? true,  // Include both cases
                    tags: editingNote.value.tags || []
                });

                // Reset editing state
                editingNote.value = null;

                // Reload notes to show updates
                await loadNotes();

                // Find the updated note and extract tasks
                const updatedNote = notes.value.find(note => note.id === noteId);
                if (updatedNote) {
                    await extractTasks(updatedNote);
                }
            } catch (error) {
                console.error('Error updating note:', error);
            }
        };

        const deleteNote = async (note) => {
            if (!confirm('Are you sure you want to delete this note?')) {
                return;
            }

            try {
                await noteService.deleteNote(note.id);
                await loadNotes();
            } catch (error) {
                console.error('Error deleting note:', error);
            }
        };

        const extractMentionedUsers = (content) => {
            if (!content) return [];
            
            try {
                const doc = typeof content === 'string' ? JSON.parse(content) : content;
                const mentions = new Set();
                
                const traverse = (node) => {
                    if (node.type === 'mention' && node.attrs) {
                        mentions.add({
                            id: node.attrs.id,
                            name: node.attrs.label
                        });
                    }
                    if (node.content) {
                        node.content.forEach(traverse);
                    }
                };
                
                traverse(doc);
                return Array.from(mentions);
            } catch (err) {
                console.error('Error extracting mentioned users:', err);
                return [];
            }
        };

        const extractTasks = async (note) => {
            
            if (!note || !note.content) {
                console.error('NotesModal.extractTasks - No note or content provided:', {
                    note: note ? JSON.stringify(note, null, 2) : null
                });
                return;
            }

            try {
                let content = note.content;
                // Content should already be parsed by this point
                if (typeof content === 'string') {
                    try {
                        content = JSON.parse(content);
                    } catch (e) {
                        console.error('NotesModal.extractTasks - Failed to parse content:', e);
                        return;
                    }
                }
                
                const mentionedUsers = extractMentionedUsers(content);
                
                // Only show task extractor if we have a valid ID and content
                if (note.id && content) {
                    taskExtractionNoteId.value = note.id;
                    taskExtractionContent.value = content;
                    taskExtractionUsers.value = mentionedUsers;
                    showTaskExtractor.value = true;
                } else {
                    console.error('NotesModal.extractTasks - Missing required data:', {
                        hasId: !!note.id,
                        hasContent: !!content
                    });
                }
            } catch (err) {
                console.error('NotesModal.extractTasks - Error:', err);
            }
        };

        const handleTasksCreated = (tasks) => {
            if (Array.isArray(tasks)) {
                emit('tasks-created', tasks);
            } else {
                console.error('NotesModal.handleTasksCreated - Invalid tasks format:', tasks);
            }
            showTaskExtractor.value = false;
        };

        const handleTaskError = (error) => {
            console.error('NotesModal.handleTaskError - Task extraction error:', error);
            showTaskExtractor.value = false;
        };

        onMounted(() => {
            loadNotes();
        });

        return {
            notes,
            loading,
            editingNote,
            isCreatingNew,
            newNote,
            isEditing,
            showTaskExtractor,
            taskExtractionNoteId,
            taskExtractionContent,
            taskExtractionUsers,
            formatDate,
            renderContent,
            startNewNote,
            cancelNewNote,
            saveNewNote,
            editNote,
            cancelEdit,
            saveEdit,
            deleteNote,
            handleTasksCreated,
            handleTaskError
        };
    }
});
</script>

<style scoped>
.modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal {
    background: white;
    border-radius: 8px;
    width: 90%;
    max-width: 600px;
    max-height: 80vh;
    overflow-y: auto;
}

.modal-header {
    padding: 1rem;
    border-bottom: 1px solid #e5e5e5;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-body {
    padding: 1rem;
}

.note-item {
    border: 1px solid #e5e5e5;
    border-radius: 4px;
    margin-bottom: 1rem;
    padding: 1rem;
}

.note-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
}

.note-meta {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.note-date {
    color: #666;
    font-size: 0.9rem;
}

.note-privacy-indicator {
    font-size: 0.8rem;
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    background-color: #e0e0e0;
    color: #666;
}

.note-privacy-indicator.private {
    background-color: #f0f0f0;
    color: #888;
}

.note-content {
    text-align: left;
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.5;
    margin-top: 0.5rem;
}

.note-actions {
    display: flex;
    gap: 0.5rem;
}

.note-actions button {
    padding: 0.25rem 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: white;
    cursor: pointer;
}

.note-actions button.delete {
    color: #dc3545;
    border-color: #dc3545;
}

.note-actions button.delete:hover {
    background: #dc3545;
    color: white;
}

.new-note-section {
    margin-top: 1rem;
    text-align: center;
}

.new-note-btn {
    padding: 0.5rem 1rem;
    background: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.new-note-btn:hover {
    background: #0056b3;
}

.editor-actions {
    margin-top: 1rem;
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}

.editor-actions button {
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
}

.editor-actions button.primary {
    background: #007bff;
    color: white;
    border: none;
}

.editor-actions button:not(.primary) {
    background: white;
    border: 1px solid #ddd;
}

.close-button {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
    color: #666;
}

.close-button:hover {
    color: #333;
}

.note-privacy {
    margin: 1rem 0;
    display: flex;
    align-items: center;
}

.note-privacy label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #666;
    font-size: 0.9rem;
}

.note-privacy input[type="checkbox"] {
    margin: 0;
}
</style>
